import React from 'react';

import './DataTable.css';
import Localizer from '../localizer';

interface DataTableColumn<T> {
	label: string;
	key?: string;
	value?: unknown | ((item: T) => unknown);
	render?: (item: T, value: unknown) => React.ReactNode;
	wrapAll?: boolean;
	noWrap?: boolean;
}

interface DataTableTool<T> {
	label?: string;
	action?: (item: T) => void;
	render?: (item: T) => React.ReactNode;
	condition?: (item: T) => boolean;
}

interface DataTableTableTool<T> {
	label?: string;
	action?: (data: T[]) => void;
	render?: (data: T[]) => React.ReactNode;
}

type ToolInput<T> = DataTableTool<T> | ((item: T) => React.ReactNode);
type TableToolInput<T> = DataTableTableTool<T> | ((data: T[]) => React.ReactNode);

interface DataTableProps<T> {
	data?: T[];
	columns?: DataTableColumn<T>[];
	tools?: ToolInput<T>[];
	tableTools?: TableToolInput<T>[];
}

function DataTable<T = Record<string, unknown>>({ data: inputData, columns: inputColumns, tools: inputTools, tableTools: inputTableTools }: DataTableProps<T>) {
	const data = inputData || [];

	const columns: DataTableColumn<T>[] = inputColumns ||
	(
		data && data.length > 0 &&
		Object.getOwnPropertyNames(data[0] as Record<string, unknown>).map(key => ({
			label: key[0].toUpperCase() + key.slice(1),
			key
		}))
	) || [];

	const tools: DataTableTool<T>[] = (inputTools || []).map(tool => {
		if (typeof tool === 'function') {
			return { render: tool };
		}

		if (tool.action && !tool.render) {
			const toolWithAction = tool;
			return {
				...tool,
				render: (item: T) => (<button type="button" className="link" onClick={() => toolWithAction.action?.(item)}>{toolWithAction.label}</button>)
			};
		}

		return tool;
	});

	const tableTools: DataTableTableTool<T>[] = (inputTableTools || []).map(tool => {
		if (typeof tool === 'function') {
			return { render: tool };
		}

		if (tool.action && !tool.render) {
			const toolWithAction = tool;
			return {
				...tool,
				render: (tableData: T[]) => (<button type="button" className="link" onClick={() => toolWithAction.action?.(tableData)}>{toolWithAction.label}</button>)
			};
		}

		return tool;
	});

	const getValue = (item: T, column: DataTableColumn<T>): unknown => {
		if (column.value) {
			if (typeof column.value === 'function') {
				return column.value(item);
			}

			return column.value;
		} else {
			const record = item as Record<string, unknown>;

			if (column.key && column.key.includes('.')) {
				const parts = column.key.split('.');
				let value: unknown = record;

				for (const part of parts) {
					if (value === undefined) {
						break;
					}

					value = (value as Record<string, unknown>)[part];
				}

				return value;
			}

			return column.key ? record[column.key] : undefined;
		}
	};

	const presentValue = (o: unknown): React.ReactNode => {
		if (typeof o === 'object') {
			if (Array.isArray(o)) {
				return `[${o.map(presentValue).join(', ')}]`;
			}

			return JSON.stringify(o, null, ' ');
		}

		if (typeof o === 'string') {
			return Localizer.getLocalizedString(String(o));
		}

		return String(o ?? '');
	};

	return (
		<div>
			<table className="dataTable">
				<tbody>
					<tr className="header">
						{
							columns && columns.map((column, columnIndex) => (
								<th key={`c${columnIndex}${column.key}`} className="nowrap">{column.label}</th>
							))
						}

						<th></th>
					</tr>
					{
						data && data.map((item, itemIndex) => (
							<tr key={`i${itemIndex}`}>
								{
									columns && columns.map((column, columnIndex) => (
										<td key={`i${itemIndex}_c${columnIndex}`} className={column.wrapAll ? 'wrapall' : column.noWrap ? 'nowrap' : ''}>
											{
												column.render
												? column.render(item, getValue(item, column))
												: presentValue(getValue(item, column))
											}
										</td>
									))
								}

								<td className="nowrap">
									{
										tools && tools
										.filter(tool => !tool.condition || tool.condition(item))
										.map((tool, toolIndex) => (
											<span key={`t${toolIndex}`} style={{marginRight: '1em'}}>
												{tool.render?.(item)}
											</span>
										))
									}
								</td>
							</tr>
						))
					}

					{
						tableTools && tableTools.length > 0 && (
							<tr className="tableTools">
								<td colSpan={(columns?.length ?? 0) + 1}>
									{
										tableTools && tableTools.map((tool, toolIndex) => (
											<React.Fragment key={`tt${toolIndex}`}>
												{tool.render?.(data)}
												{toolIndex < tableTools.length - 1 && (<span> | </span>)}
											</React.Fragment>
										))
									}
								</td>
							</tr>
						)
					}
				</tbody>
			</table>
		</div>
	);
}

export default DataTable;
export type { DataTableColumn, DataTableTool, DataTableTableTool, DataTableProps };
